import { Link } from "react-router-dom";
import { Flower, Frog, Butterfly, Trumpet, MusicNote, Heart, Scribble, Spiral, Ice } from "./Doodles";
import { SunDoodle, Cloud, Star } from "./Illustrations";
import Reveal from "./Reveal";
import MagneticButton from "./MagneticButton";

/** A vibrant Peppa-coloring-book style CTA banner. Bold colour blocks +
 *  hand-drawn doodles around an authentic lifestyle photo. */
export default function CtaBanner() {
  return (
    <section className="relative px-5 md:px-10 py-16 md:py-24 overflow-hidden" data-testid="cta-banner">
      {/* Asymmetric colour corners */}
      <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
        <div className="absolute top-0 left-0 w-1/2 h-2/3 bg-[#F4C734]" style={{ clipPath: "polygon(0 0, 100% 0, 70% 100%, 0 80%)" }} />
        <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-[#A9CDE5]" style={{ clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0 80%)" }} />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-[#8BC547]" style={{ clipPath: "polygon(0 30%, 100% 0, 100% 70%, 0 100%)" }} />
        <div className="absolute bottom-0 right-0 w-2/5 h-1/2 bg-[#F0A8C3]" style={{ clipPath: "polygon(0 70%, 100% 0, 100% 100%, 0 100%)" }} />
      </div>
      <div className="absolute inset-0 -z-10 bg-[#FAFAF7]" style={{ clipPath: "polygon(8% 12%, 92% 6%, 96% 88%, 5% 94%)" }} aria-hidden />

      {/* Scattered doodles */}
      <Flower size={64} className="absolute top-6 left-[12%] wiggle hidden md:block" color="#F4C734" center="#DD2E20" />
      <Frog size={70} className="absolute top-8 right-[20%] wiggle hidden md:block" />
      <Butterfly size={70} className="absolute bottom-12 left-[14%] float-med hidden md:block" />
      <MusicNote size={48} className="absolute top-1/3 right-[6%] float-slow hidden md:block" />
      <Trumpet size={70} className="absolute bottom-10 right-[14%] wiggle hidden lg:block" />
      <SunDoodle size={56} color="#F4C734" className="absolute bottom-1/3 left-[3%] spin-slow hidden md:block" />
      <Star size={26} className="absolute top-1/2 right-[42%]" color="#DD2E20" />
      <Star size={20} className="absolute top-[28%] left-[36%]" color="#8BC547" />
      <Heart size={38} className="absolute bottom-[22%] right-[26%] wiggle hidden md:block" />
      <Ice size={50} className="absolute top-[30%] left-[6%] float-med hidden md:block" />

      <div className="relative mx-auto max-w-[1100px] text-center">
        <Reveal>
          <p className="eyebrow mb-5">A little SOL goes a long way</p>
        </Reveal>
        <Reveal delay={60}>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-[-0.03em]">
            Bright eyes,<br />
            <span className="italic" style={{ color: "#DD2E20" }}>brighter days.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <Scribble w={150} className="mx-auto mt-6" color="#F4C734" />
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-8 text-lg md:text-xl text-[#2C2C2A]/85 max-w-[44ch] mx-auto leading-relaxed">
            Pick your kid's pair. We'll wrap it, ship it, and you'll
            spot the smile from across the room.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <MagneticButton>
              <Link
                to="/collections"
                className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] transition-colors"
                data-testid="cta-banner-shop"
              >
                Shop the collection →
              </Link>
            </MagneticButton>
            <Link
              to="/personalities"
              className="inline-flex items-center gap-2 rounded-full bg-[#FAFAF7] text-[#2C2C2A] px-7 py-4 text-[15px] font-medium border border-[#2C2C2A]/15 hover:bg-[#F3EADA] transition-colors"
              data-testid="cta-banner-personalities"
            >
              Browse personalities
            </Link>
          </div>
        </Reveal>

        {/* Lifestyle hero image card */}
        <Reveal delay={280}>
          <div className="mt-10 md:mt-14 mx-auto max-w-[680px] aspect-[16/9] rounded-[24px] overflow-hidden bg-[#F3EADA] shadow-[0_24px_60px_rgba(0,0,0,0.12)] lift" data-hover="true">
            <img src="/lifestyle/cta-banner.jpg" alt="Three Indian children walking hand-in-hand at sunset wearing SOL Kids sunglasses" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
